import {
  api,
  useLazyCreateCompanyCustomerQuery,
  useLazyFindCompanyCustomerQuery,
  useRequestCompanyAccessMutation,
} from '@api/api';
import { useForm } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import CountrySelector from '@components/CountrySelector';
import { isFetchBaseQueryError, isErrorWithMessage } from '@api/errorTypes';
import ErrorMsg from '@components/ErrorMsg';
import Row from '@components/Form/Row';
import Column from '@components/Form/Column';
import Label from '@components/Form/Label';
import { useDispatch, useSelector } from 'react-redux';
import { userState } from '@/src/redux/userSlice/userSlice';
import { RootState } from '@/src/redux/store';
import { BiSolidFactory } from 'react-icons/bi';
import useParamsEditor from '@/src/utils/useParamsEditor';
import {
  customerState,
  customerTypes,
} from '@/src/redux/customerSlice/customerSlice';
import { useCustomerSetter } from '@/src/redux/customerSlice/customerHandlers';

export default function CompanyRegistrationForm() {
  const t = useTranslations('CompanyRegistrationForm');
  const g = useTranslations('general');
  const e = useTranslations('errors');
  const editParam = useParamsEditor();
  const setCustomer = useCustomerSetter();
  const dispatch = useDispatch();
  const user: userState = useSelector((state: RootState) => {
    return state.user;
  });
  const customer: customerState = useSelector((state: RootState) => {
    return state.customer;
  });
  const [submitCreateCompany, { data: createdCompany, isFetching, isSuccess }] =
    useLazyCreateCompanyCustomerQuery();
  const [findCompanyByNumber, { data: companies, isFetching: isFinding }] =
    useLazyFindCompanyCustomerQuery();
  const [joinCompany, { isLoading: isRequesting, isSuccess: isRequested, isError }] =
    useRequestCompanyAccessMutation();
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useForm();
  const {
    register: registerNr,
    handleSubmit: handleSubmitNr,
    getValues: getValuesNr,
    setValue: setValueNr,
    formState: { errors: errorsNr },
  } = useForm();

  const [view, setView] = useState(
    'search' as 'search' | 'found' | 'requested' | 'register' | 'registered'
  );
  const [differentBilling, setDifferentBilling] = useState(false);
  const [registrationError, setRegistrationError] = useState('');

  const inputStyle = 'block w-full appearance-none rounded-sm p-2 text-dark';

  useEffect(() => {
    const defaultCountry = String(
      process.env.NEXT_PUBLIC_DEFAULT_LOCALE === 'sv'
        ? 'se'
        : process.env.NEXT_PUBLIC_DEFAULT_LOCALE
    ).toUpperCase();
    setValue('billing.country', defaultCountry);
  }, [setValue]);

  const createCompany = (data: any) => {
    data.use_billing_address = data.use_billing_address ? 1 : 0;
    if (!data.use_billing_address) {
      data.billing = null;
    }
    submitCreateCompany(data)
      .unwrap()
      .then((res: any) => {})
      .catch((res: any) => {
        if (isFetchBaseQueryError(res)) {
          if (
            res.data &&
            typeof res.data === 'object' &&
            'message' in res.data
          ) {
            setRegistrationError(
              res.data.message
                ? String(res.data.message)
                : 'Unknown error. Try again later'
            );
          }
        } else if (isErrorWithMessage(res)) {
          setRegistrationError(res.message);
        } else {
          setRegistrationError('Unknown error. Try again later');
        }
      });
  };

  const findCompany = () => {
    let orgNumber = getValuesNr('org_nr');
    findCompanyByNumber(orgNumber);
  };

  const join = () => {
    joinCompany({
      user_id: user.userId,
      company_customer_id: company.id || 0,
    });
  };

  let company = false as any;
  if (companies?.data?.length && !isFinding) {
    company = companies.data[0];
  } else if (companies && !isFinding) {
    company = false;
  }

  useEffect(() => {
    if (!companies || isFinding) {
      return;
    }
    if (companies?.data?.length) {
      setView('found');
    } else {
      setView('register');
    }
  }, [companies, isFinding]);

  useEffect(() => {
    if (isSuccess) {
      setView('registered');
    }
  }, [isSuccess]);

  useEffect(() => {
    if (isRequested) {
      setView('requested');
    }
  }, [isRequested]);

  if (view === 'search') {
    return (
      <form method='post' onSubmit={handleSubmitNr(findCompany)}>
        <h1 className='mb-4 text-3xl font-bold uppercase text-brand'>
          {t('title')}
        </h1>
        <Row>
          <Column fullWidth>
            <Label text={t('number')} />
            <input
              placeholder={t('placeholder.number')}
              className={inputStyle}
              {...registerNr('org_nr', {
                onChange: () => setValue('org_nr', getValuesNr('org_nr')),
                required: {
                  value: true,
                  message: t('required'),
                },
              })}
            />
            <ErrorMsg errors={errorsNr} name='org_nr' />
          </Column>
        </Row>
        <Row>
          <Column fullWidth>
            <button className='rounded-sm border border-brand bg-brand px-4 py-2 text-center font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'>
              {t('next')}
            </button>
          </Column>
        </Row>
        {isFinding && <>{t('searchingForCompany')}...</>}
      </form>
    );
  }

  if (view === 'register') {
    return (
      <form method='post' onSubmit={handleSubmit(createCompany)}>
        <h1 className='mb-4 text-3xl font-bold uppercase text-brand'>
          {t('register')}
        </h1>
        <input
          type='hidden'
          {...register('org_nr', {
            required: { value: true, message: t('required') },
          })}
        />
        <Row>
          <Column fullWidth>
            <Label text={t('number')} />
            <span className='text-xl font-bold'>{getValues('org_nr')}</span>
            <ErrorMsg errors={errors} name='org_nr' />
            <button
              className='mt-2 block rounded-sm border border-brand px-4 py-2 text-center font-semibold uppercase text-brand transition-all hover:text-white'
              onClick={() => setView('search')}
            >
              {t('searchAgain')}
            </button>
          </Column>
        </Row>
        <Row>
          <Column>
            <Label text={t('name')} />
            <input
              className={inputStyle}
              placeholder={t('placeholder.name')}
              {...register('name', {
                required: {
                  value: true,
                  message: t('required'),
                },
              })}
            />
            <ErrorMsg errors={errors} name='name' />
          </Column>
          <Column>
            <Label text={t('invoiceMail')} />
            <input
              className={inputStyle}
              {...register('invoice_email', {
                required: {
                  value: true,
                  message: t('required'),
                },
              })}
            />
            <ErrorMsg errors={errors} name='invoice_email' />
          </Column>
        </Row>
        <Row>
          <Column>
            <Label text={t('country')} id='country-label' />
            <CountrySelector
              placeholder={t('placeholder.selectCountry')}
              onChange={(option: any) => {
                setValue('address.country', option.value);
                trigger('address.country');
              }}
              country={
                getValues('billing.country') ||
                String(
                  process.env.NEXT_PUBLIC_DEFAULT_LOCALE === 'sv'
                    ? 'se'
                    : process.env.NEXT_PUBLIC_DEFAULT_LOCALE
                ).toUpperCase()
              }
            />
            <input
              type='hidden'
              {...register('address.country', {
                required: {
                  value: true,
                  message: t('required'),
                },
              })}
            />
            <ErrorMsg errors={errors} name='address.country' />
          </Column>
          <Column>
            <Label text={t('address')} />
            <input
              className={inputStyle}
              {...register('address.address', {
                required: {
                  value: true,
                  message: t('required'),
                },
              })}
            />
            <ErrorMsg errors={errors} name='address.address' />
          </Column>
        </Row>
        <Row>
          <Column>
            <Label text={t('postal')} />
            <input
              className={inputStyle}
              placeholder={t('placeholder.postal')}
              {...register('address.postal_code', {
                required: {
                  value: true,
                  message: t('required'),
                },
              })}
            />
            <ErrorMsg errors={errors} name='address.postal_code' />
          </Column>
          <Column>
            <Label text={t('area')} />
            <input
              className={inputStyle}
              placeholder={t('placeholder.area')}
              {...register('address.postal_area', {
                required: {
                  value: true,
                  message: t('required'),
                },
              })}
            />
            <ErrorMsg errors={errors} name='address.postal_area' />
          </Column>
        </Row>
        <Row>
          <Column>
            <label className='flex gap-1'>
              <input
                className='p-2 text-black'
                type='checkbox'
                {...register('use_billing_address', {
                  onChange: () => {
                    setDifferentBilling(!differentBilling);
                  },
                })}
              />
              {t('differentBilling')}
            </label>
          </Column>
        </Row>
        {differentBilling && (
          <>
            <Row>
              <Column>
                <Label text={t('country')} id='country-label-billing' />
                <CountrySelector
                  placeholder={t('placeholder.selectCountry')}
                  onChange={(option: any) =>
                    setValue('billing.country', option.value)
                  }
                  labelledby='country-label-billing'
                />
                <input
                  type='hidden'
                  {...register('billing.country', {
                    validate: {
                      required: (value) => {
                        if (differentBilling && !value) return t('required');
                        return undefined;
                      },
                    },
                  })}
                />
                <ErrorMsg errors={errors} name='billing.country' />
              </Column>
              <Column>
                <Label text={t('address')} />
                <input
                  className={inputStyle}
                  placeholder={t('placeholder.address')}
                  {...register('billing.address', {
                    validate: {
                      required: (value) => {
                        if (differentBilling && !value) return t('required');
                        return undefined;
                      },
                    },
                  })}
                />
                <ErrorMsg errors={errors} name='billing.address' />
              </Column>
            </Row>
            <Row>
              <Column>
                <Label text={t('postal')} />
                <input
                  className={inputStyle}
                  placeholder={t('placeholder.postal')}
                  {...register('billing.postal_code', {
                    validate: {
                      required: (value) => {
                        if (differentBilling && !value) return t('required');
                        return undefined;
                      },
                    },
                  })}
                />
                <ErrorMsg errors={errors} name='billing.postal_code' />
              </Column>
              <Column>
                <Label text={t('area')} />
                <input
                  className={inputStyle}
                  placeholder={t('placeholder.area')}
                  {...register('billing.postal_area', {
                    validate: {
                      required: (value) => {
                        if (differentBilling && !value) return t('required');
                        return undefined;
                      },
                    },
                  })}
                />
                <ErrorMsg errors={errors} name='billing.postal_area' />
              </Column>
            </Row>
          </>
        )}
        <Row>
          <Column fullWidth>
            <input
              type='hidden'
              {...register('identity_verified_at', {
                value: '2022-01-01 17:00:00',
              })}
            />
            <button
              className='rounded-sm border border-brand bg-brand px-4 py-2 text-center font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
              type='submit'
            >
              {t('register')}
            </button>
          </Column>
        </Row>
        {isFetching && <>{t('registering')}</>}
      </form>
    );
  }

  if (view === 'found') {
    return (
      <>
        <h1 className='mb-4 text-3xl font-bold uppercase text-brand'>
          {t('title')}
        </h1>
        <Row>
          <Column fullWidth>
            <Label text={t('number')} />
            <span className='text-xl font-bold'>{getValues('org_nr')}</span>
          </Column>
        </Row>
        <Label text={t('foundCompany')} />
        <span className='mb-4 mt-2 flex items-center gap-2 font-roboto-condensed text-2xl font-semibold'>
          <BiSolidFactory /> {company.name}
        </span>
        <div className='flex flex-wrap gap-4'>
          {!isRequesting && !isRequested && (
            <span>
              <button
                onClick={join}
                className='rounded-sm border border-brand bg-brand px-4 py-2 text-center font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
              >
                {t('requestAccess')}
              </button>
            </span>
          )}
          <button
            className='block rounded-sm border border-brand px-4 py-2 text-center font-semibold uppercase text-brand transition-all hover:text-white'
            onClick={() => setView('search')}
          >
            {t('searchAgain')}
          </button>
        </div>
        {isRequesting && <p className='mt-2'>{t('requestingAccess')}...</p>}
        {isError && <p className='mt-2'>{e('desc')}</p>}
      </>
    );
  }

  if (view === 'requested') {
    return (
      <div className='flex flex-col items-center gap-6'>
        <h1 className='text-3xl font-bold uppercase text-brand'>
          {t('requested')}
        </h1>
        <span className='my-2 flex items-center gap-2 font-roboto-condensed text-2xl font-semibold'>
          <BiSolidFactory /> {company.name}
        </span>
        <p className='px-4 text-center'>{t('requestedText')}</p>
        <button
          className='block rounded-sm border border-brand bg-brand px-4 py-2 text-center font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
          onClick={() => editParam('modal')}
        >
          {g('continue')}
        </button>
        <button
          className='block rounded-sm border border-brand px-4 py-2 text-center font-semibold uppercase text-brand transition-all hover:text-white'
          onClick={() => setView('search')}
        >
          {t('searchAgain')}
        </button>
      </div>
    );
  }

  if (view === 'registered') {
    return (
      <div className='flex flex-col items-center gap-6'>
        <h1 className='mb-4 text-3xl font-bold uppercase text-brand'>
          {t('registered')}
        </h1>
        <span className='flex items-center gap-2 text-xl font-bold'>
          <BiSolidFactory /> {getValues('name')}
        </span>
        <button
          className='block rounded-sm border border-brand bg-brand px-4 py-2 text-center font-roboto-condensed font-semibold uppercase text-dark transition-all hover:bg-transparent hover:text-white'
          onClick={() => {
            if (createdCompany?.data?.id) {
              setCustomer({
                id: createdCompany?.data?.id,
                type: customerTypes.company,
                name: createdCompany?.data?.name,
                newlyCreated: true,
              });
              dispatch(api.util.invalidateTags(['User']));
              editParam('modal');
            }
          }}
        >
          {t('startBuyingAs', { name: getValues('name') })}
        </button>
        <button
          className='border border-brand px-4 py-2 font-roboto-condensed font-bold uppercase text-brand hover:text-white'
          onClick={() => editParam('modal')}
        >
          {t('continueBuyingAs', { name: customer.name })}
        </button>
        <p className='mt-10'>{t('registerNextCompanyText')}</p>
        <button
          className='-mt-4 border border-brand px-4 py-2 font-roboto-condensed font-bold uppercase text-brand hover:text-white'
          onClick={() => {
            setValueNr('org_nr', '');
            reset();
            setView('search');
          }}
        >
          {t('title')}
        </button>
      </div>
    );
  }
}
